import React, { useRef, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import styled from '@emotion/styled';

const ZoneBox = styled(Box, {
    shouldForwardProp: (prop) => !['isSelected', 'fieldType'].includes(prop),
})(({ theme, isSelected, fieldType }) => {
    const colorMap = {
        text: theme.palette.primary.main,
        date: theme.palette.info.main,
        currency: theme.palette.success.main,
        table: theme.palette.warning.main,
        multiline: theme.palette.secondary.main,
    };
    const borderColor = colorMap[fieldType] || theme.palette.primary.main;

    return {
        position: 'absolute',
        border: `2px ${isSelected ? 'solid' : 'dashed'} ${borderColor}`,
        backgroundColor: isSelected ? `${borderColor}22` : `${borderColor}11`,
        cursor: 'move',
        userSelect: 'none',
        zIndex: isSelected ? 10 : 1,
        '&:hover': {
            backgroundColor: `${borderColor}22`,
        },
    };
});

const ZoneLabel = styled(Typography, {
    shouldForwardProp: (prop) => prop !== 'fieldType',
})(({ theme, fieldType }) => {
    const colorMap = {
        text: theme.palette.primary.main,
        date: theme.palette.info.main,
        currency: theme.palette.success.main,
        table: theme.palette.warning.main,
        multiline: theme.palette.secondary.main,
    };
    return {
        fontSize: theme.typography.size.xs,
        fontWeight: theme.typography.fontWeightSemiBold,
        color: colorMap[fieldType] || theme.palette.primary.main,
        lineHeight: 1,
        padding: '2px 4px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: '100%',
    };
});

const FieldZone = ({ field, scaleFactor, pageHeight, isSelected, onSelect, onMove }) => {
    const dragRef = useRef({ dragging: false, startX: 0, startY: 0, origLeft: 0, origTop: 0 });

    const screenX = field.x * scaleFactor;
    const screenY = (pageHeight - field.y) * scaleFactor;
    const screenWidth = field.width * scaleFactor;
    const screenHeight = field.height * scaleFactor;

    const handleMouseDown = useCallback((e) => {
        e.stopPropagation();
        onSelect();

        dragRef.current = {
            dragging: true,
            startX: e.clientX,
            startY: e.clientY,
            origLeft: screenX,
            origTop: screenY,
        };

        const handleMouseMove = (moveEvent) => {
            if (!dragRef.current.dragging) return;
            const dx = moveEvent.clientX - dragRef.current.startX;
            const dy = moveEvent.clientY - dragRef.current.startY;
            const newLeft = dragRef.current.origLeft + dx;
            const newTop = dragRef.current.origTop + dy;
            onMove(field.id, newLeft, newTop);
        };

        const handleMouseUp = () => {
            dragRef.current.dragging = false;
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    }, [field.id, screenX, screenY, scaleFactor, onSelect, onMove]);

    return (
        <ZoneBox
            isSelected={isSelected}
            fieldType={field.type}
            onMouseDown={handleMouseDown}
            onClick={(e) => { e.stopPropagation(); onSelect(); }}
            style={{
                left: screenX,
                top: screenY,
                width: Math.max(screenWidth, 40),
                height: Math.max(screenHeight, 16),
            }}
        >
            <ZoneLabel fieldType={field.type}>
                {field.label}
            </ZoneLabel>
        </ZoneBox>
    );
};

export default FieldZone;
